import styled from '@emotion/styled';

import { StarRating } from '@travelnest/hui';

import { VisuallyHidden } from '@/components/ui/VisuallyHidden';
import { ratingAdjective } from '@/lib/rating';
import { colors, focusRing, font, radii, spacing } from '@/lib/theme';
import { useI18n } from '@/locales';

const MAX_STARS = 5;

const Badge = styled.button`
	position: relative;
	display: inline-flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${spacing.space20};
	padding: 0;
	border: 0;
	background: none;
	${font('IR100')}
	color: ${colors.typography.body};
	cursor: pointer;

	&:focus-visible {
		${focusRing}
		border-radius: ${radii.radii20};
	}
`;

/* The score and its stars on a yellow tag, as on the original site. */
const StarTag = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${spacing.space10};
	padding: 2px ${spacing.space20};
	border-radius: ${radii.radii20};
	background: ${colors.YE20};

	strong {
		${font('IB90')}
		color: ${colors.typography.heading};
	}
`;

/* Plain words beside the tag: one coloured pill is enough. */
const Adjective = styled.span`
	${font('IM90')}
	color: ${colors.typography.heading};
`;

interface RatingBadgeProps {
	average: number;
	count: number;
	onClick?: () => void;
}

/** "4.8 ★★★★★ · Excellent", linking down to the reviews. */
export const RatingBadge = ({ average, count, onClick }: RatingBadgeProps) => {
	const t = useI18n();
	const adjective = ratingAdjective(average);
	return (
		<Badge type="button" onClick={onClick} data-testid="rating-badge">
			<StarTag>
				<strong>{average.toFixed(1)}</strong>
				<StarRating
					total={MAX_STARS}
					filled={average}
					shadow="ALL"
					range="FULL"
				/>
			</StarTag>
			{adjective && (
				<Adjective>{t(`property.reviews.${adjective}`)}</Adjective>
			)}
			{/* The whole badge links to the reviews; the count is for screen readers. */}
			<VisuallyHidden>
				{t('property.reviews.count', { count })}
			</VisuallyHidden>
		</Badge>
	);
};
